import {
  AutocompleteWithLabel,
  FreeSoloAutocompleteWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { ButtonBase, MenuItem } from '@material-ui/core'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import DeleteIcon from '@material-ui/icons/Delete'
import { FormikErrors, FormikTouched } from 'formik'
import { useEffect, useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { USERS_LIST_QUERY } from '../../graphql/userManagement'
import { WALLET_TYPES_QUERY } from '../../graphql/walletTypes'
import { getTranslate } from '../../localization'
import { Provider } from '../../types/provider'
import { BookingRedirect, SiteKeyUpload } from '../../types/sitekey'
import { UserList } from '../../types/users'
import { WalletType } from '../../types/wallet'
import { SiteKeyBookingRedirect } from '../SitekeyFormEdit/SiteKeyBookingRedirect'
import useStyles from './styles/SitekeyBasicInfo.styles'

interface SitekeyBasicInfoProps {
  errors: FormikErrors<SiteKeyUpload>
  touched: FormikTouched<SiteKeyUpload>
  handleBlur: any
  values: SiteKeyUpload
  isSubmitting: any
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  bookingRedirectsState: BookingRedirect[]
  setBookingRedirectsState: React.Dispatch<
    React.SetStateAction<BookingRedirect[]>
  >
}

const SitekeyBasicInfo: React.FC<SitekeyBasicInfoProps> = ({
  errors,
  touched,
  handleBlur,
  values,
  setFieldValue,
  bookingRedirectsState,
  setBookingRedirectsState
}) => {
  const classes = useStyles()
  const [entitiesList, setentitiesList] = useState<
    {
      label: string
      value: string
      code: string
      displayEntityCode: string
    }[]
  >([])

  const [providers, setproviders] = useState<Provider[]>([])
  const [autoCompleteLabelForEntity, setautoCompleteLabelForEntity] =
    useState<any>(null)
  const [autoCompleteLabelForUser, setautoCompleteLabelForUser] =
    useState<any>(null)
  const [autoCompleteLabelForWalletType, setAutoCompleteLabelForWalletType] =
    useState<any>({
      value: values.WalletType,
      label: getTranslate(values.WalletType)
    })

  const [getProviders, { loading: loadingForProvider }] = useLazyQuery<{
    providers: Provider[]
  }>(PROVIDERS_QUERY, {
    onCompleted: (data) => {
      setproviders(data.providers)
    }
  })

  const { data: usersData, loading: loadingForUsers } = useQuery<{
    users: UserList
  }>(USERS_LIST_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const { data: walletTypeList, loading: loadingWalletTypes } = useQuery<{
    walletTypes: WalletType[]
  }>(WALLET_TYPES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    if (providers.length === 0) {
      getProviders()
    } else {
      setentitiesList(
        providers.map((providerItem) => ({
          label: `${providerItem.name} - (${providerItem.displayProviderCode})`,
          value: providerItem.id,
          code: providerItem.providerCode,
          displayEntityCode: providerItem.displayProviderCode,
          id: providerItem.displayProviderCode
        }))
      )
    }
  }, [values.EntityType, providers])

  useEffect(() => {
    if (!loadingForProvider && values.EntityType && values.EntityId) {
      const foundProvider = providers.find(
        (provider) => provider.displayProviderCode === values.DisplayEntityCode
      )

      setautoCompleteLabelForEntity({
        label: `${foundProvider?.name} - (${foundProvider?.displayProviderCode})`,
        value: values.EntityId,
        code: values.EntityCode,
        displayEntityCode: values.DisplayEntityCode,
        id: values.DisplayEntityCode
      })
    }
  }, [values.EntityType, values.EntityId, loadingForProvider, providers])

  useEffect(() => {
    if (!loadingForUsers) {
      setautoCompleteLabelForUser({
        label: usersData?.users.users.find((user) => user.ID === values.UserId)
          ?.name,
        value: values.UserId
      })
    }
  }, [values.UserId, loadingForUsers])

  return (
    <div>
      <SectionTitle title="Basic Info" marginBottom />
      <div className={classes.inputContainer}>
        <div>
          <SelectWithLabel
            name="EntityType"
            label={getTranslate('Entity Type')}
            size="small"
            onChange={(event: { target: { value: any } }) =>
              setFieldValue('EntityType', event.target.value)
            }
            value={values.EntityType}
            error={(touched.EntityType && !!errors.EntityType) || undefined}
            helpertext={
              touched.EntityType
                ? getTranslate(errors.EntityType || '')
                : undefined
            }
            onBlur={handleBlur}>
            <MenuItem key="Provider" value="Provider">
              {getTranslate('Provider')}
            </MenuItem>
          </SelectWithLabel>
        </div>
        <div>
          <AutocompleteWithLabel
            name={'EntityId'}
            label={getTranslate('Entity')}
            placeholder={getTranslate('Select Entity')}
            value={autoCompleteLabelForEntity}
            getOptionLabel={(option) => option.label || ''}
            options={values ? entitiesList : []}
            renderOption={(option) => <>{option.label}</>}
            getOptionSelected={(option, value) => option.id === value.id}
            includeInputInList
            error={(touched.EntityId && !!errors.EntityId) || undefined}
            helpertext={
              touched.EntityId ? getTranslate(errors.EntityId || '') : undefined
            }
            onBlur={handleBlur}
            onChange={(_, v: any) => {
              setautoCompleteLabelForEntity({
                value: v.value,
                label: v.label,
                code: v.code,
                displayEntityCode: v.displayEntityCode,
                id: v.id
              })
              setFieldValue('EntityId', v.value)
              setFieldValue('EntityCode', v.code)
              setFieldValue('DisplayEntityCode', v.displayEntityCode)
            }}
            size="small"
            inputSize="small"
            loading={loadingForProvider}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div>
          <AutocompleteWithLabel
            name="UserId"
            label={getTranslate('User')}
            placeholder={getTranslate('Select a user')}
            value={autoCompleteLabelForUser}
            getOptionLabel={(option) => option.label || ''}
            options={
              usersData
                ? usersData?.users.users.map((userItem) => ({
                    label: userItem.name,
                    value: userItem.ID,
                    code: userItem.email
                  }))
                : []
            }
            renderOption={(option) => <>{option.label}</>}
            getOptionSelected={(option, value) => option.value === value.value}
            includeInputInList
            error={(touched.UserId && !!errors.UserId) || undefined}
            helpertext={
              touched.UserId ? getTranslate(errors.UserId || '') : undefined
            }
            onBlur={handleBlur}
            onChange={(_, v: any) => {
              setautoCompleteLabelForUser({
                value: v.value,
                label: v.label
              })
              setFieldValue('UserId', v.value)
            }}
            size="small"
            inputSize="small"
            loading={loadingForUsers}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div>
          <AutocompleteWithLabel
            name="WalletType"
            label={getTranslate('Wallet Type')}
            placeholder={getTranslate('Please Select Wallet Type')}
            value={autoCompleteLabelForWalletType}
            onChange={(_, value: any) => {
              if (value) {
                setFieldValue('WalletType', value.value)
                setAutoCompleteLabelForWalletType({
                  value: value.value,
                  label: value.label
                })
              }
            }}
            options={
              walletTypeList?.walletTypes
                ? walletTypeList?.walletTypes.map((wt) => {
                    return {
                      value: wt.walletType,
                      label: getTranslate(wt.walletType)
                    }
                  })
                : []
            }
            size="small"
            inputSize="small"
            error={(touched.WalletType && !!errors.WalletType) || undefined}
            helpertext={
              touched?.WalletType
                ? getTranslate(errors?.WalletType || '')
                : undefined
            }
            loading={loadingWalletTypes}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div>
          <SelectWithLabel
            name="Mode"
            label={getTranslate('Mode')}
            size="small"
            onChange={(event: { target: { value: any } }) =>
              setFieldValue('Mode', event.target.value)
            }
            value={values.Mode}
            error={(touched.Mode && !!errors.Mode) || undefined}
            helpertext={
              touched.Mode ? getTranslate(errors.Mode || '') : undefined
            }
            onBlur={handleBlur}>
            <MenuItem key="Single" value="single">
              {getTranslate('Single')}
            </MenuItem>
            <MenuItem key="Multi" value="multi">
              {getTranslate('Multi')}
            </MenuItem>
          </SelectWithLabel>
        </div>
        <div />
        <FreeSoloAutocompleteWithLabel
          name="BookingRedirects"
          label={getTranslate('BookingRedirects')}
          placeholder={getTranslate('Enter BookingRedirect')}
          onChange={() => {
            // setFieldValue('BookingRedirects', v)
            // setBookingRedirects(v)
          }}
          error={
            (touched.BookingRedirects && !!errors.BookingRedirects) || undefined
          }
          value={bookingRedirectsState
            .filter((ele) => !!ele.Code)
            .map((br) => br.BookingRedirect)}
          options={['']}
          message={''}
          regexPattern={/.*/}
          onBlur={handleBlur}
          inputSize="small"
          disabled
          disableClearable
        />
        <div />
        <div className={classes.buttonsContainer}>
          <ButtonBase
            className={[
              classes.greenColor,
              bookingRedirectsState.length > 0 && classes.greyColor
            ].join(' ')}
            onClick={() => {
              setBookingRedirectsState((preState) => {
                return [
                  ...preState,
                  {
                    Name: '',
                    BookingRedirect: '',
                    Code: ''
                  }
                ]
              })
            }}
            disabled={bookingRedirectsState.length > 0}>
            <AddCircleOutlineIcon />
          </ButtonBase>
          <ButtonBase className={classes.hide} disabled>
            <DeleteIcon />
          </ButtonBase>
        </div>
      </div>
      {bookingRedirectsState.map((br, index) => {
        return (
          <SiteKeyBookingRedirect
            key={index}
            bookingRedirectsState={bookingRedirectsState}
            index={index}
            setBookingRedirectsState={setBookingRedirectsState}
            isDefault={br.Code === 'Default'}
          />
        )
      })}
    </div>
  )
}
export default SitekeyBasicInfo
